import * as React from "react"
import { Drawer as DrawerPrimitive } from "vaul"
import { cn } from "../lib/utils"
import { useEffect, useState } from "react"

type DrawerDirection = "top" | "bottom" | "left" | "right"

interface DrawerContentProps
  extends React.ComponentProps<typeof DrawerPrimitive.Content> {
  direction?: DrawerDirection
  width?: string
}

function Drawer({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) {
  return <DrawerPrimitive.Root data-slot="drawer" {...props} />
}

function DrawerTrigger({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />
}

function DrawerPortal({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />
}

function DrawerClose({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Close>) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />
}

function DrawerOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) {
  return (
    <DrawerPrimitive.Overlay
      data-slot="drawer-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/25 backdrop-blur-[2px] transition-all duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  )
}

function DrawerContent({
  className,
  children,
  direction = "bottom",
  width = "w-full sm:max-w-md",
  ...props
}: DrawerContentProps) {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const directionClasses: Record<DrawerDirection, string> = {
    top: "inset-x-4 top-4 rounded-2xl border shadow-2xl",
    bottom: "inset-x-4 bottom-4 rounded-2xl border shadow-2xl",
    right: `top-4 bottom-4 right-4 ${width} rounded-2xl border shadow-2xl`,
    left: `top-4 bottom-4 left-4 ${width} rounded-2xl border shadow-2xl`,
  }

  const heightClasses: Record<DrawerDirection, string> = {
    top: "h-full sm:max-h-[calc(100vh-32px)]",
    bottom: "h-full sm:max-h-[calc(100vh-32px)]",
    right: "h-full sm:h-[calc(100vh-32px)]",
    left: "h-full sm:h-[calc(100vh-32px)]",
  }

  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Content
        data-slot="drawer-content"
        className={cn(
          "group/drawer-content bg-card fixed z-50 flex flex-col",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-300",
          direction === "top" && "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
          direction === "bottom" && "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
          direction === "right" && "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
          direction === "left" && "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
          directionClasses[direction],
          heightClasses[direction],
          className
        )}
        style={{
          ...(direction === "right" && { 
            right: isMobile ? "0px" : "16px", 
            top: isMobile ? "0px" : "16px", 
            bottom: isMobile ? "0px" : "16px",
            ...(isMobile && { left: "0px" })
          }),
          ...(direction === "left" && { 
            left: isMobile ? "0px" : "16px", 
            top: isMobile ? "0px" : "16px", 
            bottom: isMobile ? "0px" : "16px",
            ...(isMobile && { right: "0px" })
          }),
          ...(direction === "top" && { 
            top: isMobile ? "0px" : "16px", 
            left: isMobile ? "0px" : "16px", 
            right: isMobile ? "0px" : "16px" 
          }),
          ...(direction === "bottom" && { 
            bottom: isMobile ? "0px" : "16px", 
            left: isMobile ? "0px" : "16px", 
            right: isMobile ? "0px" : "16px" 
          }),
          ...props.style
        }}
        data-vaul-drawer-direction={direction}
        {...props}
      >
        <div className="bg-muted mx-auto mt-4 hidden h-2 w-[100px] shrink-0 rounded-full group-data-[vaul-drawer-direction=bottom]/drawer-content:block" />
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
}

function DrawerHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  )
}

function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn("mt-auto flex justify-end items-end flex-col gap-2 p-4", className)}
      {...props}
    />
  )
}

function DrawerTitle({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  )
}

function DrawerDescription({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
}
